import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  DestroyRef,
  ElementRef,
  TemplateRef,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RouterLink } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { PageEvent } from '@angular/material/paginator';
import { TableColumnConfig } from '@core/models/skeleton.model';
import { SkeletonBlockComponent } from '../skeleton/skeleton-block/skeleton-block.component';
import { TablePaginationComponent } from '../table-pagination/table-pagination.component';
import { PAGINATION_DEFAULT } from '@core/constants/pagination.constant';
import { TagComponent, TagState } from '@custom-components/general/tag/tag.component';

/** A section of a grouped table: its rows follow a header row that collapses and expands them. */
export interface TableGroup {
  /** Matched against the `groupField` value of each row. */
  key: string;
  /** A translation key, like a column label; text that is not a key is shown as it is. */
  label: string;
  /** Material Symbols name shown before the label. */
  icon?: string;
}

interface TableGroupHeader {
  groupHeader: true;
  key: string;
  label: string;
  icon?: string;
  count: number;
  expanded: boolean;
}

@Component({
  selector: 'xemex-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.compact]': 'compact()',
    '[style.--xemex-table-header-height]': 'headerHeightPx()',
  },
  imports: [
    MatTableModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatMenuModule,
    MatSlideToggleModule,
    DatePipe,
    TranslateModule,
    SkeletonBlockComponent,
    RouterLink,
    TablePaginationComponent,
    NgTemplateOutlet,
    TagComponent,
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent<T extends Record<string, unknown>> implements AfterContentInit {
  data = input.required<T[]>();
  columns = input.required<TableColumnConfig[]>();
  loading = input<boolean>(false);
  compact = input<boolean>(false);
  selectable = input<boolean>(false);
  pageable = input<boolean>(true);
  /** False drops the footer with the row count and the page controls. */
  showFooter = input<boolean>(true);
  /** Draws the cell and group icons in the outlined icon font. */
  outlinedIcons = input<boolean>(false);
  /** False leaves the number of rows out of each group row. */
  showGroupCounts = input<boolean>(true);
  pageSize = input<number>(PAGINATION_DEFAULT.limit);
  rowLink = input<((row: T) => string[]) | undefined>(undefined);
  clickableRows = input<boolean>(false);
  /** undefined = client-side pagination; number = server-side (consumer fetches on pageChange) */
  totalCount = input<number | undefined>(undefined);
  /**
   * Opt-in keyed selection. When set, selection is *controlled* by the consumer via
   * {@link selectedKeys} keyed on this function's return value, instead of the internal
   * reference-based SelectionModel. This is what lets selection survive server-side page
   * changes (where each page is a fresh set of row objects). Leave unset for the default
   * reference-based behaviour.
   */
  selectionKey = input<((row: T) => string | number) | undefined>(undefined);
  /** Controlled selection (keys) — only consulted when {@link selectionKey} is set. */
  selectedKeys = input<Set<string | number>>(new Set<string | number>());
  /**
   * Opt-in, server-side-pagination only: makes the header checkbox select/clear every row
   * matching {@link totalCount}, not just the currently loaded page. Requires {@link selectionKey}
   * and {@link totalCount} to be set — the table has no way to fetch other pages' rows itself, so
   * the consumer must resolve the actual select-all via {@link selectAllRequested}.
   */
  selectAllAcrossPages = input<boolean>(false);
  /**
   * Optional predicate marking rows that may be shown but not selected — their checkbox is
   * disabled and they're excluded from the header (select-all) toggle. Defaults to all selectable.
   */
  rowDisabled = input<((row: T) => boolean) | undefined>(undefined);
  /** When true, {@link rowDisabled} rows render with a checked (but disabled) checkbox — e.g. items already included. */
  disabledRowsChecked = input<boolean>(false);
  /** Header label for the {@link actionsCellTpl} column. Leave unset for a blank header (e.g. a kebab-menu action column). */
  actionsLabel = input<string | undefined>(undefined);
  /**
   * Opt-in grouped mode. Each row of {@link data} joins the group whose `key` equals the row's
   * {@link groupField} value; the table shows a collapsible header row per group, in this order,
   * above the group's rows. A group without rows has no header, and rows that match no group follow
   * the last group without one. A grouped table is not paged and has no footer.
   */
  groups = input<TableGroup[] | undefined>(undefined);
  groupField = input<string>('group');
  /** Width of the actions column, e.g. `'3rem'`. Pass the same value given to `xemex-table-global-row`'s `actionsWidth` so both rows agree on it — table-layout:fixed won't enforce it otherwise. */
  actionsWidth = input<string | undefined>(undefined);

  selectedRows = output<T[]>();
  /** Emits the next full selection (keys) on every toggle in keyed-selection mode. */
  selectedKeysChange = output<Set<string | number>>();
  /** Only emitted when {@link selectAllAcrossPages} is on: true = select every matching row
   * across all pages requested, false = clear the entire selection requested. */
  selectAllRequested = output<boolean>();
  pageChange = output<PageEvent>();
  rowClick = output<T>();
  /** Emits when a `toggle`-type column's slide-toggle is changed for a row. */
  toggleChange = output<{ row: T; field: string }>();
  /** Emits when a `select`-type column's menu option is chosen for a row. */
  selectChange = output<{ row: T; field: string; value: string }>();

  @ContentChild('actionsCell') protected actionsCellTpl?: TemplateRef<{ $implicit: T }>;
  /** Replaces the generic "no data" text of an empty table, e.g. to explain why it is empty. */
  @ContentChild('emptyState') protected emptyStateTpl?: TemplateRef<unknown>;
  private hasActions = signal(false);

  ngAfterContentInit(): void {
    if (this.actionsCellTpl) this.hasActions.set(true);
  }

  protected readonly skeletonDataSource = new MatTableDataSource<null>(Array(5).fill(null));
  protected dataSource = new MatTableDataSource<T | TableGroupHeader>([]);
  private collapsedGroups = signal<ReadonlySet<string>>(new Set());
  protected selection = new SelectionModel<T>(true, []);
  protected displayedColumns = computed(() => {
    const cols = this.columns().map(c => c.field);
    return this.selectable() ? ['select', ...cols] : cols;
  });

  protected allColumns = computed(() => {
    const cols = this.displayedColumns();
    return this.hasActions() ? [...cols, 'actions'] : cols;
  });

  protected currentPage = signal(1);

  protected effectiveTotalCount = computed(() => this.totalCount() ?? this.data().length);

  protected totalPages = computed(() =>
    Math.max(1, Math.ceil(this.effectiveTotalCount() / this.pageSize())),
  );

  protected pagedData = computed<(T | TableGroupHeader)[]>(() => {
    const groups = this.groups();
    if (groups) return this.groupedData(groups);
    if (!this.pageable() || this.totalCount() !== undefined) return this.data();
    const page = this.currentPage();
    const size = this.pageSize();
    return this.data().slice((page - 1) * size, page * size);
  });

  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private destroyRef = inject(DestroyRef);
  private headerHeight = signal<number | null>(null);
  /** Null until measured, so the stylesheet's own fallback applies. */
  protected headerHeightPx = computed(() => {
    const height = this.headerHeight();
    return height == null ? null : `${height}px`;
  });

  constructor() {
    effect(() => {
      this.dataSource.data = this.pagedData();
    });

    // Header labels wrap, so the sticky header's height varies with the column count and the
    // available width. Publish it as a custom property for `scroll-padding-top` to follow,
    // otherwise a snap would tuck the row under the header. The `<thead>` is CDK's own row
    // outlet container: it survives column changes, so one observer holds for the table's life.
    afterNextRender(() => {
      const thead = this.host.nativeElement.querySelector('thead');
      if (!thead) return;
      const observer = new ResizeObserver(() => this.headerHeight.set(thead.offsetHeight));
      observer.observe(thead);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  protected isGroupHeader = (_index: number, row: T | TableGroupHeader | null): boolean =>
    !!row && 'groupHeader' in row;

  protected toggleGroup(key: string): void {
    this.collapsedGroups.update(collapsed => {
      const next = new Set(collapsed);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  private groupedData(groups: TableGroup[]): (T | TableGroupHeader)[] {
    const field = this.groupField();
    const rows = this.data();
    const collapsed = this.collapsedGroups();
    const known = new Set(groups.map(group => group.key));
    const grouped = groups.flatMap(group => {
      const members = rows.filter(row => row[field] === group.key);
      if (!members.length) return [];
      const expanded = !collapsed.has(group.key);
      const header: TableGroupHeader = {
        ...group,
        groupHeader: true,
        count: members.length,
        expanded,
      };
      return expanded ? [header, ...members] : [header];
    });
    return [...grouped, ...rows.filter(row => !known.has(String(row[field])))];
  }

  protected cellValue(row: T, field: string): unknown {
    return row?.[field];
  }

  protected tagState(col: TableColumnConfig, row: T): TagState {
    return (
      ((col.stateField ? this.cellValue(row, col.stateField) : undefined) as TagState) ?? 'neutral'
    );
  }

  protected onToggleChange(col: TableColumnConfig, row: T): void {
    if (this.isCellDisabled(col, row)) return;
    this.toggleChange.emit({ row, field: col.field });
  }

  /** Whether this cell's control renders read-only — see TableColumnConfig.disabledWhen. Covers
   * both `toggle` and `select` columns, which is why it isn't named after either. */
  protected isCellDisabled(col: TableColumnConfig, row: T): boolean {
    return col.disabledWhen?.(row) ?? false;
  }

  protected onSelectOption(col: TableColumnConfig, row: T, value: string): void {
    if (this.isCellDisabled(col, row)) return;
    this.selectChange.emit({ row, field: col.field, value });
  }

  /** True when the row is shown but cannot be selected (e.g. already a member elsewhere). */
  protected isRowDisabled(row: T): boolean {
    const fn = this.rowDisabled();
    return fn ? fn(row) : false;
  }

  /** Rows eligible for selection — drives both the row checkboxes and the select-all toggle. */
  protected selectableRows = computed(() => {
    const fn = this.rowDisabled();
    const rows = this.data();
    return fn ? rows.filter(row => !fn(row)) : rows;
  });

  /** Checked state for a single row, honouring keyed (controlled) or reference-based selection. */
  protected isRowSelected(row: T): boolean {
    const keyFn = this.selectionKey();
    return keyFn ? this.selectedKeys().has(keyFn(row)) : this.selection.isSelected(row);
  }

  /** Checkbox checked state: selected rows, plus disabled rows when {@link disabledRowsChecked} is set. */
  protected isRowChecked(row: T): boolean {
    return this.isRowSelected(row) || (this.disabledRowsChecked() && this.isRowDisabled(row));
  }

  /** True once {@link selectAllAcrossPages} + {@link selectionKey} + {@link totalCount} are all
   * set — the header checkbox then reflects/drives selection across every page, not just the
   * one currently loaded. */
  private isAcrossPagesMode(): boolean {
    return this.selectAllAcrossPages() && !!this.selectionKey() && this.totalCount() !== undefined;
  }

  protected isAllSelected(): boolean {
    if (this.isAcrossPagesMode()) {
      const total = this.effectiveTotalCount();
      return total > 0 && this.selectedKeys().size === total;
    }
    const data = this.selectableRows();
    if (data.length === 0) return false;
    const keyFn = this.selectionKey();
    if (keyFn) {
      const keys = this.selectedKeys();
      return data.every(row => keys.has(keyFn(row)));
    }
    return data.every(row => this.selection.isSelected(row));
  }

  /** Header checkbox indeterminate state: something selected, but not the whole (current) page. */
  protected isIndeterminate(): boolean {
    const hasValue = this.selectionKey() ? this.selectedKeys().size > 0 : this.selection.hasValue();
    return hasValue && !this.isAllSelected();
  }

  toggleAll(): void {
    if (this.isAcrossPagesMode()) {
      this.selectAllRequested.emit(!this.isAllSelected());
      return;
    }
    const keyFn = this.selectionKey();
    const rows = this.selectableRows();
    if (keyFn) {
      const next = new Set(this.selectedKeys());
      const allSelected = this.isAllSelected();
      // Toggle only the current page's selectable rows; selections on other pages are preserved.
      rows.forEach(row => (allSelected ? next.delete(keyFn(row)) : next.add(keyFn(row))));
      this.selectedKeysChange.emit(next);
      return;
    }
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      rows.forEach(row => this.selection.select(row));
    }
    this.selectedRows.emit(this.selection.selected);
  }

  toggleRow(row: T): void {
    if (this.isRowDisabled(row)) return;
    const keyFn = this.selectionKey();
    if (keyFn) {
      const next = new Set(this.selectedKeys());
      const key = keyFn(row);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      this.selectedKeysChange.emit(next);
      return;
    }
    this.selection.toggle(row);
    this.selectedRows.emit(this.selection.selected);
  }

  protected onPageChange(page: number): void {
    if (this.totalCount() !== undefined) {
      this.pageChange.emit({
        pageIndex: page - 1,
        pageSize: this.pageSize(),
        length: this.totalCount()!,
        previousPageIndex: this.currentPage() - 1,
      });
    }
    this.currentPage.set(page);
  }

  protected onRowClick(row: T): void {
    if (this.loading()) return;
    if (this.selectable()) {
      this.toggleRow(row);
    } else if (!this.rowLink()) {
      this.rowClick.emit(row);
    }
  }
}
